// Types
import { OrderType } from '../types/orders';
import type { FormContext } from '../offline/formStorage';

// Constants
import {
  directoryNaming,
  formAccidentNaming,
} from '../components/forms/AddBasicInfoAccidentForm/constants';

// Utils
import { decompressSessionStorage } from './sessionStorage';
import {
  ACCIDENT_CREATION_DRAFT_KEYS,
  ACCIDENT_CREATION_SCOPE,
  ACCIDENT_MEDIA_DRAFT_KEYS,
  ACCIDENT_MEDIA_SCOPE,
  accidentCreationOwnerId,
} from './draftScopes';
import { draftSketchFile, type DraftSketchFile } from './sketchFile';

/**
 * El borrador del ALTA de un accidente (spec 2026-11-02
 * wizard-draft-ownership § 7, F3 «Crear accidente»; S4 de la auditoría de
 * accidentes): de qué padre es, qué fotos y qué croquis son suyos, y cómo se
 * cierra sin tocar lo de otros.
 */

type ReadableStore = Pick<Storage, 'getItem'>;
type AccidentCreationCopy = Record<string, unknown>;

/** Una copia del alta (`key`), parseada; `null` si no hay o no se puede leer. Nunca lanza. */
export const readAccidentCreationCopy = (
  key: string,
  store: ReadableStore = sessionStorage
): AccidentCreationCopy | null => {
  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const copy = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    return copy && typeof copy === 'object' && !Array.isArray(copy)
      ? (copy as AccidentCreationCopy)
      : null;
  } catch {
    return null;
  }
};

/**
 * La copia del paso 1 (`new-accident-form`) SOLO si es del padre `parentId`
 * (el `:id` de la URL): lo dice su `orderParentId`, que escribe la puerta
 * («Crear accidente» → `pass-data`). De otro padre, sin padre o ilegible →
 * `null`.
 *
 * Tras `claimDraftScope(ACCIDENT_CREATION_SCOPE, parent:<:id>)` el borrador ya
 * es de la URL; esto es el cinturón: los pasos 2 y 3 sacaban el padre de esta
 * copia sin mirar la URL (auditoría § 4, `AddVehicle/loader.ts:27-57`).
 */
export const readAccidentCreationDraft = (
  parentId: string | undefined,
  store: ReadableStore = sessionStorage
): AccidentCreationCopy | null => {
  if (!parentId) return null;
  const draft = readAccidentCreationCopy(formAccidentNaming.NEW_ACCIDENT_FORM, store);
  return draft?.orderParentId === parentId ? draft : null;
};

/**
 * Nombres de las fotos del alta: el campo `images` de la copia del paso 2
 * (`add-basic-info-accident-form`), la LISTA que pinta `AccidentAttention`
 * (`setValue('images', …)` + `useCacheFormInSessionStorage`). Es lo único que
 * sube el alta.
 *
 * 🔴 No la carpeta `accident` de OPFS: es de todo el origen, la comparten las
 * pestañas y la edición de accidentes, y desde la Task 7 la edición ya no la
 * vacía al guardar. Subir «lo que haya» se llevaba fotos de otro accidente a
 * este. Tampoco `selected-accidents-files`: nadie la escribe (Ruling 6).
 */
export const accidentCreationPhotoNames = (
  basicInfo: { images?: unknown } | null | undefined
): string[] => {
  let images: unknown = basicInfo?.images;
  if (typeof images === 'string') {
    try {
      images = JSON.parse(images);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(images)) return [];
  return images
    .map((image: { name?: unknown; id?: unknown } | null) => image?.name ?? image?.id)
    .filter((name): name is string => typeof name === 'string' && name.length > 0);
};

/** El croquis del alta: el campo `sketch` de la copia del paso 2, con su `fileName`. */
export const accidentCreationSketch = (
  basicInfo: { sketch?: unknown } | null | undefined
): DraftSketchFile => draftSketchFile(basicInfo?.sketch);

/**
 * Cierra el alta del padre `parentId` tras guardarla (o encolarla): borra SOLO
 * lo suyo.
 *
 * Antes era `removeNewAccidentStorage`, que tiraba también la EDICIÓN de
 * accidente a medias de la pestaña y vaciaba las carpetas compartidas
 * `accident` y `canvas-accident` — las fotos y croquis sin guardar de otros
 * borradores y de otras pestañas (Ruling 5). Ahora:
 *
 * - Las tres copias del alta y su dueño (`ACCIDENT_CREATION_SCOPE`).
 * - Las claves de sesión del croquis (`ACCIDENT_MEDIA_SCOPE`), solo si su dueño
 *   es este alta: si no, son de otro asistente.
 * - De la carpeta `accident`, las fotos que listaba el alta: ya están en la
 *   bandeja (`offline-pending-uploads`) o en la operación encolada.
 * - De `canvas-accident`, el croquis que se llevó este guardado
 *   (`sketchFileName`): por nombre, nunca la carpeta.
 *
 * El contexto de navegación NO se toca: lo escribe el guardado justo después.
 */
export const releaseAccidentCreationDraft = async (
  form: FormContext,
  parentId: string | undefined,
  { sketchFileName = null }: { sketchFileName?: string | null } = {}
): Promise<void> => {
  const photoNames = accidentCreationPhotoNames(
    readAccidentCreationCopy(formAccidentNaming.ADD_BASIC_INFO_ACCIDENT_FORM, form.store)
  );

  ACCIDENT_CREATION_DRAFT_KEYS.forEach((key) => form.store.removeItem(key));
  form.store.removeItem(ACCIDENT_CREATION_SCOPE.ownerKey);

  const owner = accidentCreationOwnerId(parentId);
  if (owner && form.store.getItem(ACCIDENT_MEDIA_SCOPE.ownerKey) === owner) {
    ACCIDENT_MEDIA_DRAFT_KEYS.forEach((key) => form.store.removeItem(key));
    form.store.removeItem(ACCIDENT_MEDIA_SCOPE.ownerKey);
  }

  await Promise.all([
    ...photoNames.map((name) =>
      form.images.remove(OrderType.Accident, name).catch(() => undefined)
    ),
    ...(sketchFileName
      ? [form.images.remove(directoryNaming.CANVAS, sketchFileName).catch(() => undefined)]
      : []),
  ]);
};
